import React, {
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import classNames from 'classnames';
import { Field, Form } from 'react-final-form';
import { Button, Loader } from 'ncoded-component-library';
import arrayMutators from 'final-form-arrays';
import SideDrawer from 'router/subrouters/DataRoom/components/SideDrawer';
import useCallbackRef from 'hooks/useCallbackRef';
import { useTranslation } from 'react-i18next';
import { DataRoomNode } from 'router/subrouters/DataRoom/models/DataRoomNode';
import { SideDrawerRef } from 'router/subrouters/DataRoom/components/SideDrawer/SideDrawer.component';
import GroupAccess from './components/GroupAccess';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import FileInfo from './components/FileInfo';
import FileSecurity from './components/FileSecurity';
import { DataRoomDocumentHistory } from './components/DataRoomDocumentHistory';
import { BulkAvailabilityMembersForm } from 'router/subrouters/DataRoom/models/DataRoomBulk';
import { FieldArray } from 'react-final-form-arrays';
import {
  prepareFormData,
  prepareResponseForFormData,
  processDataForUpdatedValues,
} from './utils';
import AllBuySidesGroupAccess from './components/AllBuySidesGroupAccess';
import ExpandIcon from 'icons/Expand.icon';
import useMultiCallbackRef from 'hooks/useMultiCallbackRef';
import { CollapsibleRef } from 'ncoded-component-library/build/components/molecules/Collapsible/Collapsible.component';
import MembersContext from 'router/subrouters/DataRoom/providers/Members/Members.context';
import {
  getArrayLength,
  getCanShareNode,
  getIsNodeFile,
  getIsRevisedVersion,
  isSingleElementArray,
  scrollToErrors,
  scrollToElement,
} from 'utils';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';
import CheckToggleField from 'components/CheckToggleField';
import useInvalidateFilePreviewCache from 'hooks/useInvalidateFilePreviewCache';
import { ALIGNMENT } from 'models/Global';
import { isFileSecurityRestrictedToBasic } from 'router/subrouters/DataRoom/utils';
import SoftSignalsLockedHint from 'router/subrouters/DataRoom/components/SoftSignalsLockedHint';
import useLeadStagesCopy from 'hooks/useLeadStagesCopy';
import {
  confirmRemoveLeadsFromAutomation,
  confirmStopLeadsOrder,
} from 'modules/releaseOrderConfirm';
import useGetMixpanelProps from 'hooks/useGetMixpanelProps';
import mixpanelService from 'services/mixpanelService';
import {
  AUTOMATION_REMOVAL_SHARED_FROM,
  AutomationRemovalCancelledEventProps,
} from 'models/MixpanelEvents';
import { LeadStages } from 'models/Lead';
import ReleaseOrderShareNote from './components/ReleaseOrderShareNote';

import './ManageFileAccess.styles.scss';

const BUY_SIDE_ACCESS_CLASS = 'manage-file-access__buy-side-access';

export type ManageFileAccessApi = Pick<
  typeof dataRoomApi.nodes,
  | 'bulkGetManageDetails'
  | 'bulkUpdateManageDetails'
  | 'previewReleaseOrderShare'
>;

type ManageFileAccessProps = {
  className?: string;
  nodes: DataRoomNode[];
  parentId: string;
  /**
   * Which client the two availability routes go through. Defaults to the Data Room
   * one; `DataRoomFileAccess` passes the platform copies so the drawer also works
   * outside the Data Room, where there is no Data Room token.
   */
  api?: ManageFileAccessApi;
  scrollToBuySide?: boolean;
  updateNodes?: (nodes: DataRoomNode[]) => void;
};

const ManageFileAccess: React.ForwardRefRenderFunction<
  SideDrawerRef,
  ManageFileAccessProps
> = (props, ref) => {
  const {
    className,
    nodes,
    parentId,
    scrollToBuySide = false,
    api = dataRoomApi.nodes,
    updateNodes,
  } = props;

  const [isLoading, setIsLoading] = useState(true);
  const [nodesForSubmit, setNodesForSubmit] = useState<Partial<DataRoomNode>[]>(
    [],
  );
  const [releaseOrderSteps, setReleaseOrderSteps] = useState<LeadStages[]>([]);

  const fileIdsForSubmit = useMemo(
    () => nodesForSubmit.map(({ _id }) => _id),
    [nodesForSubmit],
  );

  const [drawer, drawerRef] = useCallbackRef<SideDrawerRef>(null);

  const { invalidateFile } = useInvalidateFilePreviewCache();

  const { sellSideMembers, buySideGroups } = useContext(MembersContext);
  const {
    dataRoom: { accessConfirmationDays, dealRoom },
    isBuySide,
  } = useContext(DataRoomContext);

  const { getCopies } = useLeadStagesCopy();

  const getMixpanelProps = useGetMixpanelProps();

  const stageCopies = useMemo(
    () => getCopies(dealRoom?.leadStagesCopies),
    [dealRoom?.leadStagesCopies, getCopies],
  );

  const [groupsCollapsible, groupsCollapsibleRef] =
    useMultiCallbackRef<CollapsibleRef>();

  const classes = classNames('manage-file-access', className);

  const nodesLength = getArrayLength(nodes);
  const isSelectedOneFile = isSingleElementArray(nodes);
  const firstElement = nodes[0];
  const isSingleFile = isSelectedOneFile && getIsNodeFile(firstElement);

  const isRestrictedToBasic = isFileSecurityRestrictedToBasic(nodes);

  const haveFileHistory =
    isSelectedOneFile && getIsRevisedVersion(firstElement.version);

  const hasBuySideGroups = !!buySideGroups?.length;

  const hasMoreBuySideGroups = useMemo(
    () => hasBuySideGroups && buySideGroups?.length > 1,
    [hasBuySideGroups, buySideGroups?.length],
  );

  const areAllBuySideGroupsOpen = useMemo(
    () => groupsCollapsible?.every((group) => group?.open),
    [groupsCollapsible],
  );

  const fileIds = useMemo(() => nodes.map(({ _id }) => _id), [nodes]);

  // One file in a "One at a time" order locks the whole selection.
  const isSoftSignalsLocked = useMemo(
    () => nodesForSubmit.some(({ isSoftSignalsLocked }) => isSoftSignalsLocked),
    [nodesForSubmit],
  );

  const formData = useMemo(
    () =>
      nodesForSubmit?.length &&
      prepareFormData(
        prepareResponseForFormData({
          sellSideMembers,
          buySideGroups,
          accessConfirmationDays,
          dataRoomNodes: nodesForSubmit,
        }),
      ),
    [nodesForSubmit, buySideGroups, sellSideMembers, accessConfirmationDays],
  );

  const { popServerError, popSuccess } = useContext(PopNotificationsContext);

  useImperativeHandle(ref, () => drawer, [drawer]);

  const { t } = useTranslation();

  const collapseBuySideGroups = useCallback(
    (e) => {
      e.stopPropagation();
      groupsCollapsible?.map((group) =>
        group.setOpen(!areAllBuySideGroupsOpen),
      );
    },
    [areAllBuySideGroupsOpen, groupsCollapsible],
  );

  const handleOnSubmit = useCallback(
    async (values: BulkAvailabilityMembersForm) => {
      try {
        const newData = processDataForUpdatedValues(values, formData);

        let removeFromAutomation = false;

        // Every node is locked in a "One at a time" deal, the only mode a share can skip or stop an order in.
        if (isSoftSignalsLocked) {
          const { data: preview } = await api.previewReleaseOrderShare(
            fileIdsForSubmit,
            newData,
            parentId,
          );
          const { leads, leadsCount, step } = preview;

          if (leadsCount) {
            const shouldShare = await confirmRemoveLeadsFromAutomation({
              leadNames: leads.map(({ name }) => name),
              leadsCount,
              filesCount: getArrayLength(fileIdsForSubmit),
              stage: step && stageCopies[step],
            });

            if (!shouldShare) {
              mixpanelService.automationRemovalCancelled(
                getMixpanelProps({
                  dealId: dealRoom?._id,
                  dealName: dealRoom?.name,
                  leadIds: leads.map(({ _id }) => _id),
                  leadNames: leads.map(({ name }) => name),
                  sharedFrom: AUTOMATION_REMOVAL_SHARED_FROM.SHARE_DRAWER,
                }) as AutomationRemovalCancelledEventProps,
              );
              return;
            }

            removeFromAutomation = true;
          }

          if (!(await confirmStopLeadsOrder(preview, stageCopies))) return;
        }

        const { data } = await api.bulkUpdateManageDetails(
          fileIdsForSubmit,
          { ...newData, ...(removeFromAutomation && { removeFromAutomation }) },
          parentId,
        );

        popSuccess(t('successfullyUpdatedSettings', { count: nodesLength }));
        updateNodes?.(data);
        fileIdsForSubmit.forEach(invalidateFile);
        drawer?.setOpen(false);
      } catch (e) {
        popServerError(e);
      }
    },
    [
      api,
      formData,
      fileIdsForSubmit,
      isSoftSignalsLocked,
      stageCopies,
      parentId,
      nodesLength,
      drawer,
      dealRoom,
      getMixpanelProps,
      updateNodes,
      invalidateFile,
      popServerError,
      popSuccess,
      t,
    ],
  );

  useEffect(() => {
    if (!drawer?.open) return;

    (async () => {
      try {
        setIsLoading(true);
        setReleaseOrderSteps([]);

        const { data } = await api.bulkGetManageDetails(fileIds);
        const shareableNodes = data.dataRoomNodes.filter(getCanShareNode);

        setNodesForSubmit(shareableNodes);

        // Only "One at a time" has orders; an empty share just reads which ones hold the selection.
        if (
          shareableNodes.some(({ isSoftSignalsLocked }) => isSoftSignalsLocked)
        ) {
          const {
            data: { releaseOrderSteps },
          } = await api.previewReleaseOrderShare(
            shareableNodes.map(({ _id }) => _id),
            { changedPermissions: [] },
            parentId,
          );

          setReleaseOrderSteps(releaseOrderSteps || []);
        }
      } catch (e) {
        popServerError(e);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [api, drawer?.open, fileIds, parentId, popServerError]);

  useEffect(() => {
    if (!scrollToBuySide || !drawer?.open || isLoading) return;

    const timeout = setTimeout(
      () => scrollToElement(`.${BUY_SIDE_ACCESS_CLASS}`),
      0,
    );

    return () => clearTimeout(timeout);
  }, [scrollToBuySide, drawer?.open, isLoading]);

  return (
    <SideDrawer
      ref={drawerRef}
      className={classes}
      title={t('shareFiles', { count: nodesLength })}
      lazyContent
      closeAction={() => setNodesForSubmit([])}
    >
      <FileInfo nodes={nodes} />

      {!isLoading && (
        <ReleaseOrderShareNote
          steps={releaseOrderSteps}
          isSingleFile={isSingleFile}
          stageCopies={stageCopies}
        />
      )}

      {isLoading ? (
        <Loader />
      ) : (
        <Form
          mutators={{ ...arrayMutators }}
          initialValues={formData}
          keepDirtyOnReinitialize
          onSubmit={handleOnSubmit}
          render={({
            submitting,
            pristine,
            values: { fileSecurity },
            hasValidationErrors,
            handleSubmit,
          }) => {
            const disabledSubmit = submitting || pristine;

            return (
              <form onSubmit={handleSubmit}>
                {submitting && <Loader />}

                <div
                  className={classNames(
                    'manage-file-access__soft-signals',
                    'white-panel',
                    {
                      'manage-file-access__soft-signals--locked':
                        isSoftSignalsLocked,
                    },
                  )}
                >
                  <p className="title">{t('softSignals')}</p>

                  <Field
                    name="allowSoftSignals.checked"
                    component={CheckToggleField}
                    type="checkbox"
                    label={t('allowSoftSignals')}
                    disabled={isSoftSignalsLocked}
                  />

                  {isSoftSignalsLocked && <SoftSignalsLockedHint />}
                </div>

                <FileSecurity
                  title={t('fileSecurity')}
                  levelsLayout={ALIGNMENT.HORIZONTAL}
                  {...(!isSelectedOneFile &&
                    !fileSecurity && {
                      fileSecurityWarning: t('fileSecurityMixedNote'),
                    })}
                  {...(isRestrictedToBasic && {
                    fileSecurityWarning: t('fileSecurityBasicOnly'),
                    isDisabled: isRestrictedToBasic,
                  })}
                />

                <div className={BUY_SIDE_ACCESS_CLASS}>
                  {isBuySide ? (
                    <FieldArray name="buySideGroups">
                      {({ fields }) =>
                        fields.map((name, index) => {
                          const { isSingleMemberGroup } =
                            fields.value[index] || {};

                          return (
                            <GroupAccess
                              ref={groupsCollapsibleRef(index)}
                              key={name}
                              name={`${name}.dataRoomMembers`}
                              isSingleMemberGroup={isSingleMemberGroup}
                              title={t('yourBuySideGroup')}
                            />
                          );
                        })
                      }
                    </FieldArray>
                  ) : (
                    hasBuySideGroups && (
                      <div className="manage-file-access__buy-groups">
                        <div>
                          <p>{t('allBuyerGroups')}</p>

                          {hasMoreBuySideGroups && (
                            <Button
                              icon={ExpandIcon}
                              variant="link"
                              iconPosition="right"
                              onClick={collapseBuySideGroups}
                            >
                              {t(
                                areAllBuySideGroupsOpen
                                  ? 'collapseAllGroups'
                                  : 'expandAllGroups',
                              )}
                            </Button>
                          )}
                        </div>

                        <AllBuySidesGroupAccess />

                        <FieldArray name="buySideGroups">
                          {({ fields }) =>
                            fields.map((name, index) => {
                              const { name: groupName, isSingleMemberGroup } =
                                fields.value[index] || {};

                              return (
                                <GroupAccess
                                  ref={groupsCollapsibleRef(index)}
                                  key={name}
                                  name={`${name}.dataRoomMembers`}
                                  isSingleMemberGroup={isSingleMemberGroup}
                                  title={
                                    isSingleMemberGroup
                                      ? t(groupName)
                                      : groupName
                                  }
                                />
                              );
                            })
                          }
                        </FieldArray>
                      </div>
                    )
                  )}
                </div>

                <GroupAccess
                  name="sellSideMembers"
                  title={t('sellSideMembers')}
                  hasFileAccess={false}
                />

                {haveFileHistory && (
                  <DataRoomDocumentHistory
                    node={firstElement}
                    canFetch={drawer?.open}
                  />
                )}

                <p className="notify">{t('updateFileAccessNotify')}</p>

                <div className="manage-file-access__actions">
                  <Button variant="link" onClick={() => drawer?.setOpen(false)}>
                    {t('cancel')}
                  </Button>
                  <Button
                    type="submit"
                    disabled={disabledSubmit}
                    onClick={() => {
                      if (hasValidationErrors) scrollToErrors();
                    }}
                  >
                    {t('save')}
                  </Button>
                </div>
              </form>
            );
          }}
        />
      )}
    </SideDrawer>
  );
};

export default forwardRef(ManageFileAccess);
